import { v4 as uuidv4 } from 'uuid';
import { createStorage, prefixStorage } from 'unstorage';
import indexedDbDriver from 'unstorage/drivers/indexedb';
import sessionStorageDriver from 'unstorage/drivers/session-storage';
import apiFetch from '@wordpress/api-fetch';

/**
 * Page view tracking class.
 */
export class PageViewTracker {
	/**
	 * @type {Object} IndexedDB storage instance
	 */
	pageViewStorage;

	/**
	 * @type {Object} Session storage instance
	 */
	sessionStorage;

	/**
	 * @type {Object} Prefixed page storage instance
	 */
	pageStorage;

	/**
	 * @type {Object} Prefixed user session storage instance
	 */
	userSession;

	/**
	 * @type {string[]} Array of allowed post types for tracking
	 */
	allowedPostTypes;

	/**
	 * @type {string} API endpoint for regions
	 */
	endpoint;

	/**
	 * @type {string} External API for region detection
	 */
	regionApi;

	/**
	 * @type {number} Time window in milliseconds for unique view detection
	 */
	TIME_WINDOW;

	/**
	 * @type {Promise<string>} Promise resolving to user's region
	 */
	userRegion;

	/**
	 * Constructor to initialize storage and settings.
	 */
	constructor() {
		// Initialize storage
		this.pageViewStorage = createStorage({ driver: indexedDbDriver({ base: 'app' }) });
		this.sessionStorage = createStorage({ driver: sessionStorageDriver({ base: 'app' }) });

		// Prefix storage to avoid conflicts
		this.pageStorage = prefixStorage(this.pageViewStorage, 'page');
		this.userSession = prefixStorage(this.sessionStorage, 'session');

		this.allowedPostTypes = ['post', 'publication', 'story'];
		this.endpoint = '/we/v2/regions';
		this.regionApi = 'https://get.geojs.io/v1/ip/geo.json';
		this.TIME_WINDOW = 15 * 60 * 1000; // 15 minutes
		this.userRegion = this.getUserRegion();
	}

	/**
	 * Check if view is unique based on time window.
	 * @param {number} postId Post ID to check.
	 * @return {Promise<boolean>} True if unique, false otherwise.
	 */
	isTimeBasedUnique = async (postId) => {
		const now = Date.now();
		const record = await this.pageStorage.getItem(postId);
		return !record || now - record.timestamp > this.TIME_WINDOW;
	}

	/**
	 * Check if view is unique in current session.
	 * @param {number} postId Post ID to check.
	 * @return {Promise<boolean>} True if unique, false otherwise.
	 */
	isSessionBasedUnique = async (postId) => {
		let sessionId = await this.userSession.getItem('session_id');
		if (!sessionId) {
			sessionId = uuidv4();
			await this.userSession.setItem('session_id', sessionId);
		}

		const sessionRecord = await this.userSession.getItem(sessionId) || {};
		return !sessionRecord[postId];
	}

	/**
	 * Initialize tracking.
	 *
	 * This method checks for post data, gets the user's country/region,
	 * and tracks the page view if post type conditions are met.
	 */
	init = async () => {
		try {
			const postData = this.getPostData();

			if (!postData) {
				return;
			}

			const region = await this.userRegion;
			await this.trackPageView(postData.id, region, postData.type);
		} catch (error) {
			throw new Error(`Failed to initialize tracking: ${error}`);
		}
	}

	/**
	 * Get current post data.
	 *
	 * @return {Object|null} Post data or null if not found.
	 */
	getPostData = () => {
		let postId = null;
		let postType = null;

		// Try to get data from WordPress editor
		if (typeof wp !== 'undefined' && wp.data) {
			postId = wp.data.select('core/editor')?.getCurrentPostId?.();
			postType = wp.data.select('core/editor')?.getCurrentPostType?.();
		}

		// Fallback to body classes
		if (!postId || !postType) {
			const body = document.body;

			if (body) {
				postId = body?.dataset?.pageId || null;
				postType = body?.dataset?.pageType || null;
			}
		}

		return postId && postType ? { id: parseInt(postId), type: postType } : null;
	}

	/**
	 * Get user's region from GeoJS API.
	 *
	 * @return {Promise<string>} Region code.
	 */
	getUserRegion = async () => {
		// Check if user region is stored in sessionStorage.
		const userRegion = sessionStorage.getItem( 'wec-geo-location' );

		// Return region.
		if ( userRegion ) {
			return userRegion;
		}

		// Get user region via API endpoint.
		try {
			const response = await apiFetch({
				path: `${this.endpoint}/current`,
				method: 'GET',
			});

			const { region:userRegion } = response;

			// Save user location in DOM sessionStorage
			// for usage in featured-regional-content/view.js.
			// This prevents the need of having to bundle unstorage twice.
			sessionStorage.setItem( 'wec-geo-location', userRegion );

			// Return region.
			return userRegion;
		} catch ( error ) {
			console.error( `Failed to fetch user region: ${ error }` );
		}
	}

	/**
	 * Get popular posts for a specific region.
	 *
	 * @param {string} region Region code.
	 * @return {Promise<Array>} Array of popular posts.
	 */
	getPopularPosts = async (region, postType = '') => {
		try {
			const response = await apiFetch({
				path: `/we/v2/regions/${ region }/posts/popular${ postType ? `/${postType}` : '' }`,
				method: 'GET',
			});

			return response;
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Failed to fetch posts:', error );
			return [];
		}
	}

	/**
	 * Track page view for a post.
	 *
	 * @param {number} postId   Post ID.
	 * @param {string} region   Region code.
	 * @param {string} postType Post type.
	 */
	trackPageView = async (postId, region, postType) => {
		try {
			if (!this.allowedPostTypes.includes(postType)) {
				return;
			}

			if (await this.isTimeBasedUnique(postId) && await this.isSessionBasedUnique(postId)) {
				// Update storage
				await this.pageStorage.setItem(postId, { timestamp: Date.now(), region });

				let sessionId = await this.userSession.getItem('session_id');
				const sessionRecord = await this.userSession.getItem(sessionId) || {};

				// Record view in session
				sessionRecord[postId] = Date.now();
				await this.userSession.setItem(sessionId, sessionRecord);

				// Send to API
				apiFetch.use( apiFetch.createNonceMiddleware( wpApiSettings.nonce ) );
				const response = await apiFetch({
					path: `${this.endpoint}/${region}/posts/${postId}/views`,
					method: 'POST',
				});

				if (! response?.success) {
					console.error( 'Error tracking page view' );
				}

				console.log('View tracked');
			} else {
				console.log('View already recorded in session or still within the set time limit.');
			}
		} catch (error) {
			throw new Error(`Error tracking page view: ${error}`);
		}
	}

	/**
	 * Get validated user region, falling back to 'USA' if not available.
	 *
	 * @param {string} region User's region.
	 * @return {Promise<string>} Validated region code.
	 */
	getValidatedUserRegion = async (region) => {
		if (region) {
			return region;
		}

		return await this.userRegion;
	}
}

// Initialize tracking.
(async () => {
	const tracker = new PageViewTracker();
	window.wecGeoPosts = (region, postType = '') => tracker.getPopularPosts(region, postType) ?? [];
	await tracker.init();
})().catch(error => {
	console.error('Failed to initialize page view tracking: ', error);
});
